/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import RadioButton, { RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import { Option, Select } from 'carbon-react/lib/components/select';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Button from 'carbon-react/lib/components/button';

import { ColorPicker } from '../../../forecast/detail/components/color-picker';
import { useReportBuilderContext } from '../../report-builder.context';
import { PageSetup, PAGE_SIZES } from '../../types';
import { pageSizesOptions } from '../../constants';
import { useReportsContext } from '../../reports.context';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './page-setup-menu.scss';

interface Props {
  toggleMenu: (open: boolean) => void;
}
export const PageSetupMenu = ({ toggleMenu }: Props) => {
  const intl = useIntl();
  const palette = ['#fff', '#EAECEF', '#D9DCE3', '#CCD6DB', '#ABABAB', '#668494'];

  const { performanceDashboards } = useReportsContext();
  const {
    pageSetup,
    setPageSetup,
    setMargins,
    setBackgroundColor,
    setPageOrientation,
    setPageSize,
    setShowMargins,
    updateReportTitle,
    reportId
  } = useReportBuilderContext();
  const { margins, backgroundColor, pageSize, orientation, showMargins } = pageSetup;
  const { reports } = useReportsContext();
  const { getHighestUntitledReportNum, orientations, marginOptions } = useI18nConstants();

  const [isPerformanceDashboard, setIsPerformanceDashboard] = useState(false);

  useEffect(() => {
    setIsPerformanceDashboard(!!performanceDashboards.find(pd => pd.report_id === reportId));
  }, [performanceDashboards, reportId]);

  // original state
  const origPageSetup = useRef<PageSetup>(pageSetup);

  const handlePortraitModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newOrientation = event.target.value as 'portrait' | 'landscape' | 'dashboard';
    setPageOrientation(newOrientation);
  };

  const handleShowMarginsChange = () => setShowMargins(!showMargins);

  const handleBackgroundChange = (color: string) => {
    setBackgroundColor?.(color);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(e.target.value as PAGE_SIZES);
  };

  const handleMarginsChange = (lab: string, value: string) => {
    const newMargins = margins?.map(m => (m.label === lab ? { ...m, value: +value } : { ...m }));
    setMargins(newMargins);
  };

  // const onSaveAsDefault = () => {
  //   const pageSetupInfo = {
  //     orientation,
  //     showMargins,
  //     backgroundColor,
  //     pageSize,
  //     margins
  //   };
  //   localStorage.setItem('page-setup-info', JSON.stringify(pageSetupInfo));
  //   return;
  // };

  const oldOrientation = origPageSetup.current.orientation;
  const onSave = () => {
    if (oldOrientation === 'dashboard' && (orientation === 'landscape' || orientation === 'portrait')) {
      const highestURNbr = getHighestUntitledReportNum(reports, orientation);
      updateReportTitle(
        intl.formatMessage(
          { id: 'reporting.untitled_report_n', defaultMessage: 'Untitled Report {n}' },
          { n: highestURNbr + 1 }
        )
      );
    }
    if ((oldOrientation === 'landscape' || oldOrientation === 'portrait') && orientation === 'dashboard') {
      const highestURNbr = getHighestUntitledReportNum(reports, orientation);
      updateReportTitle(
        intl.formatMessage(
          { id: 'reporting.untitled_dashboard_n', defaultMessage: 'Untitled Dashboard {n}' },
          { n: highestURNbr + 1 }
        )
      );
    }
    origPageSetup.current = {
      orientation,
      showMargins,
      backgroundColor,
      pageSize,
      margins,
      zoom: origPageSetup.current.zoom,
      pageNumbersDisplay: origPageSetup.current.pageNumbersDisplay
    };
    toggleMenu(false);
  };

  const onCancel = () => {
    setPageSetup(origPageSetup.current);
    toggleMenu(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  return (
    <aside
      aria-label={intl.formatMessage({ id: 'reporting.page_setup_menu.aria_label', defaultMessage: 'Page setup menu' })}
    >
      <div className={clsx(styles.menuContainer)} onClick={e => handleClick(e)}>
        <div>
          <h2 className={clsx(styles.menuItemHeader, styles.subtitle)}>
            <FormattedMessage id="reporting.orientation" defaultMessage="Orientation" />
          </h2>
          <div className={clsx(styles.radioButtonGroup)}>
            <RadioButtonGroup
              aria-labelledby="page-orientation-radio-group-label"
              name="page-orientation-radio-group"
              onChange={handlePortraitModeChange}
              value={orientation}
              inline
            >
              <RadioButton
                value="portrait"
                label={orientations.portrait}
                inputHint={
                  isPerformanceDashboard && (
                    <FormattedMessage
                      id="reporting.cannot_change_orientation"
                      defaultMessage="You can't change orientation for a dashboard that is added to performance area"
                    />
                  )
                }
                checked={orientation === 'portrait'}
                disabled={isPerformanceDashboard}
              />
              <RadioButton
                value="landscape"
                label={orientations.landscape}
                inputHint={
                  isPerformanceDashboard && (
                    <FormattedMessage
                      id="reporting.cannot_change_orientation"
                      defaultMessage="You can't change orientation for a dashboard that is added to performance area"
                    />
                  )
                }
                checked={orientation === 'landscape'}
                disabled={isPerformanceDashboard}
              />
              <RadioButton value="dashboard" label={orientations.dashboard} checked={orientation === 'dashboard'} />
            </RadioButtonGroup>
          </div>
        </div>
        <div className={clsx(styles.background)}>
          <h2 className={clsx(styles.menuItemHeader)}>
            <FormattedMessage id="reporting.background" defaultMessage="Background" />
          </h2>
          <div className={clsx(styles.colorContainer)}>
            <ColorPicker
              id="page-background-color"
              color={backgroundColor}
              onChange={handleBackgroundChange}
              presetColors={palette}
            />
          </div>
        </div>
        {orientation !== 'dashboard' && (
          <div className={styles.paperSize}>
            <h2 className={clsx(styles.menuItemHeader)}>
              <FormattedMessage id="reporting.paper_size" defaultMessage="Paper size" />
            </h2>
            <Select size="large" value={pageSize} autoComplete="off" onChange={handlePageSizeChange} maxWidth="226px">
              {pageSizesOptions.map(option => (
                <Option key={option.value} value={option.value} text={option.label} />
              ))}
            </Select>
          </div>
        )}
        {orientation !== 'dashboard' && (
          <div className={clsx(styles.margins)}>
            <h2 className={clsx(styles.menuItemHeader)}>
              <FormattedMessage id="reporting.margins" defaultMessage="Margins" />
              <span style={{ fontWeight: 400, color: 'rgba(0, 0, 0, 0.65)' }} className={styles.noMarginSpan}>
                {' ('}
                <FormattedMessage id="reporting.inches" defaultMessage="inches" />
                {')'}
              </span>
            </h2>
            {margins?.map(({ label, value }) => (
              <span key={label} className={clsx(styles.marginInputsContainer)}>
                <label>{marginOptions[label as keyof typeof marginOptions]}</label>
                <input
                  type="number"
                  min={0.1}
                  placeholder={`${value}`}
                  onChange={e => handleMarginsChange(label, e.target.value)}
                />
              </span>
            ))}
            <div className={clsx(styles.showMargins)}>
              <h2 className={clsx(styles.menuItemHeader)} style={{ justifyContent: 'space-between' }}>
                <FormattedMessage id="reporting.show_margins" defaultMessage="Show margins" />
                <Checkbox
                  aria-labelledby="Show or hide report margins"
                  checked={showMargins}
                  onChange={handleShowMarginsChange}
                />
              </h2>
            </div>
          </div>
        )}
        <div className={clsx(styles.buttonsContainer)}>
          <div className={clsx(styles.leftSideBtn)}>
            {/* <button className={clsx(styles.saveAsDefaultBtn)} onClick={onSaveAsDefault}>
            Save as default
          </button> */}
          </div>
          <div className={clsx(styles.rightSideBtn)}>
            <Button buttonType="secondary" className={clsx(styles.cancelBtn)} onClick={onCancel} size="small">
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <Button buttonType="primary" className={clsx(styles.saveBtn)} onClick={onSave} size="small">
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
};
